import { Routine, createRoutine } from 'redux-saga-routines';
import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
    Routines,
    AnyRecord,
    RoutineReducers,
    CreateRoutineSliceArgument,
    ReducersMapExtendable,
    LocationChangeAction,
    TopLevelSelectors,
} from './createRoutineSlice.types';
import withKeyProps from './withKeyProps';

/**
 * @deprecated we're moving to native redux-toolkit usage, without routines or saga.
 *
 * This will be kept for as long as needed, but I want deprecation to be visible.
 */
export default function createBuilderCallbackAndRoutines<
    N extends string,
    S extends AnyRecord,
    RM extends undefined | ReducersMapExtendable<S, string>,
    E extends ReadonlyArray<string> | undefined = undefined,
>({
    name,
    initialState,
    extraRoutines,
    routineReducers = {} as Exclude<RM, undefined>,
    initialBuilderCallback,
    onLocationChange,
}: CreateRoutineSliceArgument<N, S, RM, E>) {
    const routines = {} as Routines<RM, E>;

    const createRoutineCallback = (routineName: string) => {
        routines[routineName as keyof Routines<RM, E>] = createRoutine(
            `${name}/${routineName}`,
        ) as Routine<any>;
    };
    Object.keys(routineReducers).forEach(createRoutineCallback);
    (extraRoutines as E)?.forEach(createRoutineCallback);

    const builderCallback = (builder: ActionReducerMapBuilder<S>) => {
        initialBuilderCallback?.(builder);

        Object.entries(routineReducers).forEach(([routineName, routineCaseReducers]) => {
            Object.entries(routineCaseReducers as RoutineReducers<S>).forEach(
                ([routineStage, caseReducer]) => {
                    builder.addCase(
                        (routines[routineName as keyof Routines<RM, E>] as AnyRecord)[routineStage],
                        (state, action: PayloadAction) => caseReducer(state, action.payload),
                    );
                },
            );
        });

        if (onLocationChange) {
            builder.addCase('@@router/LOCATION_CHANGE', (state, action: LocationChangeAction) =>
                onLocationChange(state, action.payload),
            );
        }
    };

    const { reducer, getInitialState } = createSlice({
        name,
        initialState,
        reducers: {},
        extraReducers: builderCallback,
    });

    let selectorsObj: TopLevelSelectors<S, N>;

    return {
        routines,
        getInitialState,
        reducer: withKeyProps(reducer, name),
        builderCallback,

        get selectors() {
            if (!selectorsObj) {
                selectorsObj = Object.keys(initialState).reduce(
                    (acc, key: keyof S) => {
                        acc[key] = state => (state[name] || getInitialState())[key];
                        return acc;
                    },
                    {} as TopLevelSelectors<S, N>,
                );
            }
            return selectorsObj;
        },
    };
}
